import type { Map as MlMap } from "maplibre-gl";

/**
 * Briques de la capture d'image de la carte (voir `MapLibreAdapter.captureImage`), isolées ici pour
 * la même raison que `backgroundTileLayer.ts` : elles sont paramétrées par la carte/le contexte
 * qu'on leur passe plutôt que de lire `this.*`, donc testables sans MapLibre ni WebGL.
 */

/** Transformation affine 2D, dans l'ordre des 6 arguments de `CanvasRenderingContext2D.setTransform`
 * — le même que celui de la fonction CSS `matrix()`, voir `parseCssMatrix2d`. */
export type Affine2d = [number, number, number, number, number, number];

const IDENTITY: Affine2d = [1, 0, 0, 1, 0, 0];

function parseNumbers(args: string, expected: number): number[] | null {
  const values = args.split(",").map((part) => Number(part.trim()));
  if (values.length !== expected || !values.every(Number.isFinite)) return null;
  return values;
}

/**
 * Lit la `transform` CSS posée sur un `<img>` du `shape-image-overlay` (voir
 * `MapLibreAdapter.applyImageOverlayTransform`) pour la rejouer sur un canvas 2D.
 *
 * `matrix3d()` (utilisée sous pitch réel, où le quadrilatère n'est plus un parallélogramme) n'a pas
 * d'équivalent sur un contexte 2D, qui ne connaît que l'affine : on n'en garde alors que les
 * composantes affines (m11/m12/m21/m22 + translation), ce qui replace l'image au bon endroit mais
 * sans sa perspective. Accepté : l'outil est 2D, le pitch reste un cas de bord (voir
 * `FLAT_PITCH_THRESHOLD_DEG`), et une image légèrement redressée vaut mieux qu'une image absente.
 */
export function parseCssMatrix2d(transform: string): Affine2d | null {
  const value = transform.trim();
  if (value === "" || value === "none") return IDENTITY;

  const matrix2d = /^matrix\((.*)\)$/.exec(value);
  if (matrix2d) {
    const values = parseNumbers(matrix2d[1]!, 6);
    return values ? (values as Affine2d) : null;
  }

  const matrix3d = /^matrix3d\((.*)\)$/.exec(value);
  if (matrix3d) {
    const v = parseNumbers(matrix3d[1]!, 16);
    return v ? [v[0]!, v[1]!, v[4]!, v[5]!, v[12]!, v[13]!] : null;
  }

  return null;
}

/**
 * Réduit `width`/`height` pour tenir dans `maxWidth`/`maxHeight` en conservant le ratio — jamais
 * d'agrandissement : une capture ne peut pas inventer les pixels que le rendu n'a pas produits.
 */
export function fitWithin(
  width: number,
  height: number,
  maxWidth?: number,
  maxHeight?: number,
): { width: number; height: number } {
  const ratio = Math.min(
    1,
    maxWidth && maxWidth > 0 ? maxWidth / width : 1,
    maxHeight && maxHeight > 0 ? maxHeight / height : 1,
  );
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * Délai au-delà duquel une capture renonce à attendre sa frame. Le rendu d'une carte MapLibre est
 * piloté par `requestAnimationFrame`, qu'un navigateur ne sert PAS à un onglet caché (arrière-plan,
 * fenêtre minimisée) : `triggerRepaint` n'y produit jamais rien, et l'attente durerait indéfiniment.
 * Un appelant qui capture en quittant un écran doit récupérer une erreur, pas une promesse en
 * suspens.
 */
const CAPTURE_FRAME_TIMEOUT_MS = 5_000;

/**
 * Recopie le canvas WebGL d'une carte MapLibre dans `ctx`, mis à l'échelle de `width`/`height`.
 *
 * La copie se fait DANS le callback `"render"`, et c'est tout l'enjeu : aucune des deux cartes n'est
 * construite avec `preserveDrawingBuffer` (coût mémoire + une copie par frame pendant toute la
 * session d'édition, pour un besoin ponctuel), donc leur tampon de dessin est effacé dès que le
 * navigateur a composité la frame. Lu depuis le callback, on est encore dans la même tâche que le
 * rendu : le tampon est intact. `triggerRepaint` garantit qu'une frame arrive, même sur une carte au
 * repos qui n'en produirait plus aucune.
 */
export function drawMapCanvasInto(
  map: MlMap,
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const onRender = () => {
      clearTimeout(timer);
      try {
        const canvas = map.getCanvas();
        // Un tampon de dessin de taille nulle veut dire que cette carte ne peint rien (contexte WebGL
        // indisponible, conteneur sans taille au moment de sa construction) : `drawImage` échouerait
        // sur un `InvalidStateError` qui ne dit pas d'où il vient.
        if (!canvas.width || !canvas.height) {
          throw new Error("[captureImage] la carte n'a aucun rendu à capturer (tampon de dessin vide)");
        }
        ctx.drawImage(canvas, 0, 0, width, height);
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    const timer = setTimeout(() => {
      map.off("render", onRender);
      reject(new Error("[captureImage] aucune frame rendue — page en arrière-plan ?"));
    }, CAPTURE_FRAME_TIMEOUT_MS);

    map.once("render", onRender);
    map.triggerRepaint();
  });
}

/** Résout au prochain "idle" de `map`. Une carte déjà au repos ne rend plus aucune frame, donc
 * n'émettrait plus jamais "idle" : `triggerRepaint` en provoque une, après laquelle il arrive. */
export function waitForMapIdle(map: MlMap): Promise<void> {
  return new Promise((resolve) => {
    map.once("idle", () => resolve());
    map.triggerRepaint();
  });
}

/**
 * Recharge une image en mode CORS pour pouvoir la dessiner dans un canvas dont on veut ensuite
 * relire les pixels.
 *
 * Les `<img>` du `shape-image-overlay` sont affichés sans `crossOrigin` (rien ne l'exige pour un
 * simple affichage) : les dessiner tels quels "tainterait" le canvas, et `toBlob` lèverait une
 * `SecurityError` — toute la capture serait perdue à cause d'une seule image. On en recharge donc
 * une copie propre (le navigateur la sert depuis son cache quand l'hôte renvoie déjà les en-têtes
 * CORS), et l'appelant saute celles qui échouent plutôt que de tout perdre.
 *
 * Rend `null` sur échec — y compris quand l'hôte ne renvoie pas d'`Access-Control-Allow-Origin`, ou
 * quand la réponse déjà en cache a été obtenue sans CORS.
 */
export function loadImageForCanvas(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    // Une `data:` URI ne tainte jamais, et lui imposer une requête CORS n'aurait pas de sens.
    if (!src.startsWith("data:")) image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

/**
 * Dessine les images-formes SVG (`<img>` DOM montés derrière le canvas, voir
 * `MapLibreAdapter.syncShapeImages`) dans `ctx`, à l'échelle `scale` (pixels de sortie par pixel
 * CSS). Chacune est replacée par sa propre `transform`, qui projette déjà sa taille naturelle sur le
 * quadrilatère de sa forme à l'écran.
 *
 * Rend les ids des formes dont l'image n'a pas pu être dessinée (voir `loadImageForCanvas`) — à
 * remonter à l'appelant : une capture à laquelle il manque un plan doit pouvoir se dire.
 */
export async function drawOverlayImagesInto(
  ctx: CanvasRenderingContext2D,
  overlays: Map<string, HTMLImageElement>,
  scale: number,
): Promise<string[]> {
  const skipped: string[] = [];

  for (const [shapeId, mounted] of overlays) {
    if (!mounted.complete || mounted.naturalWidth === 0 || mounted.style.visibility === "hidden") continue;

    const matrix = parseCssMatrix2d(mounted.style.transform);
    if (!matrix) {
      skipped.push(shapeId);
      continue;
    }

    const image = await loadImageForCanvas(mounted.src);
    if (!image) {
      skipped.push(shapeId);
      continue;
    }

    const [a, b, c, d, e, f] = matrix;
    ctx.save();
    ctx.globalAlpha = Number(mounted.style.opacity || "1");
    // La `transform` mappe la taille NATURELLE de l'image sur son quadrilatère en pixels CSS : la
    // mise à l'échelle vers la taille de sortie se compose simplement par-dessus.
    ctx.setTransform(a * scale, b * scale, c * scale, d * scale, e * scale, f * scale);
    // Dimensions prises sur l'image MONTÉE, celle pour laquelle la matrice a été calculée : une
    // copie qui rapporterait une taille naturelle différente (SVG sans dimensions intrinsèques) se
    // dessinerait sinon à côté.
    ctx.drawImage(image, 0, 0, mounted.naturalWidth, mounted.naturalHeight);
    ctx.restore();
  }

  return skipped;
}

/** `HTMLCanvasElement.toBlob` en promesse — `null` (encodage refusé) devient une erreur, pour ne pas
 * rendre une capture vide sans rien dire. */
export function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(`[captureImage] encodage impossible en ${type}`))),
      type,
      quality,
    );
  });
}
